import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Navbar from "../components/Navbar";

// --- helpers RUT ---
const stripDotsHyphen = (s="") => s.replace(/\./g, "").replace(/-/g, "").toUpperCase();
const isRutLike = (s="") => /^[0-9.\-kK]+$/.test(s) && s.replace(/[^0-9K]/gi,"").length >= 2;

// xx.xxx.xxx-x
const formatRutPretty = (input="") => {
  let v = input.toUpperCase().replace(/[^0-9K]/g, "");
  if (v.length < 2) return input;
  const dv = v.slice(-1);
  const cuerpo = v.slice(0, -1);
  let out = "";
  let c = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    out = cuerpo[i] + out;
    c++;
    if (c % 3 === 0 && i !== 0) out = "." + out;
  }
  return `${out}-${dv}`;
};

// tres variantes: pretty, sin puntos (con guión), sin nada (cuerpo+dv)
const makeRutVariants = (raw) => {
  const basic = raw.trim();
  if (!basic) return {};
  const pretty = formatRutPretty(basic);           // 12.345.678-9
  const noDots = pretty.replace(/\./g, "");        // 12345678-9
  const compact = stripDotsHyphen(pretty);         // 123456789 (si DV numérico) o 12345678K
  return { pretty, noDots, compact };
};

// --- debounce hook ---
const useDebounced = (value, ms=300) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
};

const BuscarRut = () => {
  const [busqueda, setBusqueda] = useState("");
  const debouncedTerm = useDebounced(busqueda, 300);

  const [resultados, setResultados] = useState([]);
  const [sugerencias, setSugerencias] = useState([]);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cancelRef = useRef(null);

  // --- Sugerencias mientras se escribe ---
  useEffect(() => {
    setError(null);
    if ((debouncedTerm || "").length < 3) {
      setSugerencias([]);
      return;
    }

    if (cancelRef.current) cancelRef.current.cancel("cancel prev");
    cancelRef.current = axios.CancelToken.source();

    axios
      .get(
        `https://garibcasinos.com/api/sugerencias-rut-nombre.php`,
        { params: { termino: debouncedTerm }, cancelToken: cancelRef.current.token }
      )
      .then((res) => {
        // espero un array de strings; si no, ajusta aquí:
        const arr = Array.isArray(res.data) ? res.data : [];
        setSugerencias(arr);
      })
      .catch((err) => {
        if (!axios.isCancel(err)) setSugerencias([]);
      });

    return () => {
      if (cancelRef.current) cancelRef.current.cancel("cleanup");
    };
  }, [debouncedTerm]);

  // --- Buscar (por RUT o por nombre) ---
  const buscar = async (valor) => {
    if (!valor) return;
    setCargando(true);
    setError(null);
    setResultados([]);

    if (cancelRef.current) cancelRef.current.cancel("cancel prev");
    cancelRef.current = axios.CancelToken.source();

    // decidir si es RUT o nombre
    const isRut = isRutLike(valor);
    let params = {};
    if (isRut) {
      const { pretty, noDots, compact } = makeRutVariants(valor);
      // Probamos por rut (formato con guión) y si el backend espera sin puntos:
      // Sugerencia: que tu PHP busque ambas variantes (LIKE %compact% y LIKE %noDots%).
      params = { rut: pretty };
      try {
        const res = await axios.get(
          `https://garibcasinos.com/api/buscar-rut.php`,
          { params, cancelToken: cancelRef.current.token }
        );
        const data = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
        if (data.length) {
          setResultados(data);
        } else {
          // reintento con noDots:
          const res2 = await axios.get(
            `https://garibcasinos.com/api/buscar-rut.php`,
            { params: { rut: noDots }, cancelToken: cancelRef.current.token }
          );
          const data2 = Array.isArray(res2.data) ? res2.data : (res2.data?.rows || []);
          if (data2.length) setResultados(data2);
          else {
            // reintento con compact:
            const res3 = await axios.get(
              `https://garibcasinos.com/api/buscar-rut.php`,
              { params: { rut: compact }, cancelToken: cancelRef.current.token }
            );
            const data3 = Array.isArray(res3.data) ? res3.data : (res3.data?.rows || []);
            setResultados(data3);
          }
        }
      } catch (e) {
        if (!axios.isCancel(e)) setError("Error al buscar el RUT.");
      }
    } else {
      // nombre
      try {
        const res = await axios.get(
          `https://garibcasinos.com/api/buscar-rut.php`,
          { params: { nombre: valor }, cancelToken: cancelRef.current.token }
        );
        const data = Array.isArray(res.data) ? res.data : (res.data?.rows || []);
        setResultados(data);
      } catch (e) {
        if (!axios.isCancel(e)) setError("Error al buscar por nombre.");
      }
    }

    setCargando(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    buscar(busqueda);
  };

  const exportarExcel = () => {
    window.open(
      `https://garibcasinos.com/api/buscar-rut.php?${isRutLike(busqueda) ? "rut" : "nombre"}=${encodeURIComponent(busqueda)}&exportar=1`,
      "_blank"
    );
  };

  const handleSeleccion = (valor) => {
    setBusqueda(valor);
    setSugerencias([]);
    buscar(valor);
  };

  // pequeña ayuda visual: muestra cómo se interpretará el término
  const interpretacion = useMemo(() => {
    if (!busqueda) return "";
    if (isRutLike(busqueda)) {
      const { pretty, noDots, compact } = makeRutVariants(busqueda);
      return `RUT → ${pretty} | ${noDots} | ${compact}`;
    }
    return "Búsqueda por Nombre";
  }, [busqueda]);

  return (
    <>
      <section className="section">
        <div className="container">
          <h1 className="title">Buscar Tickets por RUT o Nombre</h1>
          <form onSubmit={handleSubmit} className="box" style={{ position: "relative" }}>
            <div className="field has-addons">
              <div className="control is-expanded">
                <input
                  className="input"
                  type="text"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Ingrese RUT (12.345.678-9) o nombre"
                  required
                  autoComplete="off"
                />
                {!!sugerencias.length && (
                  <div className="box"
                       style={{ position: 'absolute', zIndex: 10, width: 'calc(100% - 2rem)', maxHeight: 240, overflowY: 'auto' }}>
                    {sugerencias.map((s, idx) => (
                      <div key={idx}
                           className="dropdown-item"
                           style={{ cursor: 'pointer', padding: '6px 8px' }}
                           onClick={() => handleSeleccion(s)}>
                        {s}
                      </div>
                    ))}
                  </div>
                )}
                <p className="help">{interpretacion}</p>
              </div>
              <div className="control">
                <button className={`button is-primary ${cargando ? "is-loading" : ""}`} type="submit">Buscar</button>
              </div>
              <div className="control">
                <button className="button" type="button" onClick={exportarExcel}>Exportar</button>
              </div>
            </div>
          </form>

          {error && <div className="notification is-danger">{error}</div>}

          {resultados.length > 0 ? (
            <table className="table is-fullwidth is-striped mt-4">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>RUT</th>
                  <th>Área</th>
                  <th>Centro de Costo</th>
                  <th>Fecha</th>
                </tr>
              </thead>
              <tbody>
                {resultados.map((r, i) => (
                  <tr key={i}>
                    <td>{r.nombre || r.Nombre}</td>
                    <td>{r.rut || r.Rut}</td>
                    <td>{r.area || r.AreaTrabajo}</td>
                    <td>{r.centro_costo || r.CentroCosto}</td>
                    <td>{r.fecha_ticket || r.fecha || r.Fecha}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            busqueda && !error && !cargando && (
              <div className="notification is-warning mt-4">
                No hay registros disponibles para la búsqueda ingresada.
              </div>
            )
          )}
        </div>
      </section>
    </>
  );
};

export default BuscarRut;
